import type Phaser from "phaser";
import { textStyle } from "./textStyles";

/**
 * Нижний диалоговый бокс с typewriter-эффектом ~30 cps.
 * Пробел или клик — пропуск к концу строки.
 */
export default class DialogBox {
  private container: Phaser.GameObjects.Container;
  private text: Phaser.GameObjects.Text;
  private typing?: Phaser.Time.TimerEvent;
  private fullText = "";
  private resolveFn?: () => void;
  private skipHandler?: () => void;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    this.container = scene.add.container(x, y);
    this.container.setDepth(100);
    const frame = scene.add.image(0, 0, "ui_dialog_box").setOrigin(0, 0);
    this.container.add(frame);

    this.text = scene.add.text(8, 8, "", textStyle(8, "#000000", { wordWrap: { width: 460 } }));
    this.container.add(this.text);

    this.skipHandler = () => this.skip();
    scene.input.keyboard?.on("keydown-SPACE", this.skipHandler);
    scene.input.keyboard?.on("keydown-ENTER", this.skipHandler);
    scene.input.on("pointerdown", this.skipHandler);
  }

  /** Печать строки. Возвращает Promise по завершению (или после пропуска). */
  show(line: string): Promise<void> {
    this.typing?.remove();
    this.fullText = line;
    this.text.setText("");
    return new Promise((resolve) => {
      this.resolveFn = resolve;
      let idx = 0;
      this.typing = this.text.scene.time.addEvent({
        delay: 33,
        loop: true,
        callback: () => {
          idx++;
          this.text.setText(this.fullText.slice(0, idx));
          if (idx >= this.fullText.length) {
            this.typing?.remove();
            this.typing = undefined;
            this.finishTyping();
          }
        },
      });
    });
  }

  /** Мгновенно показать всю строку. */
  skip(): void {
    if (this.typing) {
      this.typing.remove();
      this.typing = undefined;
      this.text.setText(this.fullText);
      this.finishTyping();
    }
  }

  /**
   * Мгновенно перезаписать текст (без typewriter и без resolve обещания).
   * Используется для бокса описания во время выбора атаки.
   */
  setText(line: string, wrapWidth?: number): void {
    this.typing?.remove();
    this.typing = undefined;
    this.fullText = line;
    if (wrapWidth !== undefined) {
      this.text.setWordWrapWidth(wrapWidth);
    }
    this.text.setText(line);
  }

  private finishTyping(): void {
    const r = this.resolveFn;
    this.resolveFn = undefined;
    if (r) r();
  }

  setVisible(v: boolean): void {
    this.container.setVisible(v);
  }
}
